import React from 'react';

import LoginView from './components/LoginView';
import SignupView from './components/SignupView';
import ForgotPasswordView from './components/ForgotPasswordView';
import ForgotPasswordCompleteView from './components/ForgotPasswordCompleteView';
import HomeView from './components/HomeView';
import PlayerView from './components/PlayerView';
import JukeboxView from './components/JukeboxView';
import ControlPanelView from './components/ControlPanelView';
import NotFoundView from './components/NotFoundView';
import InviteView from './components/InviteView';
import AdminView from './components/AdminView';
import AnalyticsConsent from './components/AnalyticsConsent';
import LegalView from './components/LegalView';
import VerifyEmailView from './components/VerifyEmailView';
import VenuesView from './components/VenuesView';

import { RequestContext } from './components/NetworkRequest';
import { getQueryParam } from './Utils';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { trackPageView } from './utils/Analytics';

/** Context */
import AuthContextProvider from './contexts/AuthContext';

import Public from './routes/Public';
import Authenticated from './routes/Authenticated';

const onEnterPlayerPage = (nextState) => {
  // Session.setPersistent('selectedPlayerIdentifier', nextState.params.playerIdentifier);
};

/** Reads :playerIdentifier off the URL, which used to arrive via the route's match prop. */
const PlayerRoute = () => {
  const { playerIdentifier } = useParams<{ playerIdentifier: string }>();
  // Lowercased for the same reason as JukeboxRoute below.
  return <PlayerView playerIdentifier={(playerIdentifier as string).toLowerCase()} />;
};

/** The customer screen for the same player, read off the URL the way PlayerRoute does. */
const JukeboxRoute = () => {
  const { playerIdentifier } = useParams<{ playerIdentifier: string }>();
  // Codes are generated lowercase but the player screen prints them in capitals, so
  // capitals are what somebody typing one off that screen into their phone will send.
  // The lookup matches the column exactly, and the socket's room is named after it too,
  // so an uppercased code found no player and joined a room nothing ever published to.
  return <JukeboxView playerIdentifier={(playerIdentifier as string).toLowerCase()} />;
};

/** The owner's control panel for the same player. Lowercased for the same reason. */
const ControlPanelRoute = () => {
  const { playerIdentifier } = useParams<{ playerIdentifier: string }>();
  return <ControlPanelView playerIdentifier={(playerIdentifier as string).toLowerCase()} />;
};

/** Reports a page view each time the address changes. Renders nothing. */
const PageViews = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const App = () => {
  const path = window.location.pathname;

  return (
    <AuthContextProvider>
      <PageViews />
      <AnalyticsConsent />
      <Routes>
        <Route
          path="/"
          element={
            <Public>
              <LoginView />
            </Public>
          }
        />
        <Route
          path="/signup"
          element={
            <Public>
              <SignupView />
            </Public>
          }
        />
        <Route
          path="/recover-password"
          element={
            <Public>
              <ForgotPasswordView />
            </Public>
          }
        />
        <Route
          path="/recover-password-complete"
          element={
            <Public>
              <ForgotPasswordCompleteView />
            </Public>
          }
        />
        <Route
          path="/home"
          element={
            <Authenticated>
              <HomeView />
            </Authenticated>
          }
        />
        <Route
          path="/player/:playerIdentifier"
          element={
            <Authenticated>
              <PlayerRoute />
            </Authenticated>
          }
        />
        <Route
          path="/jukebox/:playerIdentifier"
          element={
            <Authenticated>
              <JukeboxRoute />
            </Authenticated>
          }
        />
        <Route
          path="/control/:playerIdentifier"
          element={
            <Authenticated>
              <ControlPanelRoute />
            </Authenticated>
          }
        />
        {/* The owner's dashboard for all of Jookable. Admin accounts only. */}
        <Route
          path="/admin"
          element={
            <Authenticated>
              <AdminView />
            </Authenticated>
          }
        />
        {/* A player admin invite. Signed-out visitors sign in or sign up first and come
            back here. */}
        <Route
          path="/invite/:token"
          element={
            <Authenticated>
              <InviteView />
            </Authenticated>
          }
        />
        {/* Where the link in the sign-up email lands. Open signed in or not, so the link
            works whoever is signed in on this browser. */}
        <Route path="/verify-email" element={<VerifyEmailView />} />
        {/* The Terms of Service and Privacy Policy. Open to everyone, signed in or not. */}
        <Route path="/terms" element={<LegalView document="terms" />} />
        <Route path="/privacy" element={<LegalView document="privacy" />} />
        {/* Venues near you: the public map. Open to everyone, signed in or not. */}
        <Route path="/venues" element={<VenuesView />} />
        {/* Anything else, and last so it is read as the fallback. Without this
            react-router renders nothing for an address it does not know, which is a
            solid white page with no error in the console - impossible to tell apart
            from a broken deploy. Deliberately not wrapped in Authenticated: a wrong
            address should say so whether or not you are signed in, rather than
            bouncing to the login screen. */}
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </AuthContextProvider>
  );
};

export default App;
